/** @jsxRuntime classic */
/** @jsx jsx */
import { gql, useQuery } from '@apollo/client';
import { Title } from '@bottlebooks/gatsby-design-system';
import { Col, Container } from '@bottlebooks/gatsby-theme-base/src';
import { Trans } from '@lingui/macro';
import deepMerge from 'lodash/merge';
import { jsx } from 'theme-ui';
import useLink from '../../useLink';
import BrandAddressSection, {
  BrandAddressSectionFragment,
} from '../Brand/BrandAddressSection';
import BrandBanner, { BrandBannerFragment } from '../Brand/BrandBanner';
import BrandShopsSection, {
  BrandShopsSectionFragment,
} from '../Brand/BrandShopsSection';
import ContentRow from '../ContentRow';
import ExhibitorActions from '../Exhibitor/ExhibitorActions';
import ExhibitorHeaderRow, {
  ExhibitorHeaderRowFragment,
} from '../ExhibitorPage/ExhibitorHeaderRow';
import ExhibitorSections from '../ExhibitorPage/ExhibitorSections';
import PreviewLayout from '../Layout/PreviewLayout';
import ProductCard from '../ProductCard/ProductCard';
import ProductListListRow from '../ProductListsList/ProductListListRow';
import ProductListsList from '../ProductListsList/ProductListsList';
import ProductsList from '../ProductsList/ProductsList';

export default function ExhibitorPreviewPage({
  companyId,
  collectionId,
  locale,
}) {
  const productLists = [];
  const link = useLink();
  const { data, loading, error /* , refetch */ } = useQuery(clientQuery, {
    variables: { collectionId, locale, companyId },
  });
  if (loading) return <PreviewLayout locale={locale}>Loading…</PreviewLayout>;
  if (error) {
    console.error(error);
    return (
      <PreviewLayout locale={locale}>
        We're sorry, but there was an error.
      </PreviewLayout>
    );
  }

  const { exhibitor, products } = afterQuery(data);
  const hasProducts = products && products.length !== 0;
  const hasProductLists = productLists?.length > 0;
  const hasProductsSection = hasProducts || hasProductLists;
  return (
    <PreviewLayout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <BrandBanner {...exhibitor} />
      <ExhibitorHeaderRow exhibitor={exhibitor} />

      <Container>
        <ContentRow sx={{ paddingY: 3 }}>
          <Col>
            <ExhibitorActions exhibitor={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <ExhibitorSections exhibitor={exhibitor} eventId={collectionId} />

      <BrandAddressSection brand={exhibitor} />

      <Container>
        <ContentRow>
          <Col>
            <BrandShopsSection brand={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      {hasProductsSection ? (
        <Container fluid sx={{ maxWidth: 'container.fluid' }}>
          <Title sx={{ paddingY: 4 }}>
            <Trans>Products</Trans>
          </Title>
          {hasProductLists && (
            <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
              {productLists.map((productList) => (
                <ProductListListRow
                  key={productList.id}
                  productList={productList}
                  to={link.productList(productList)}
                  sx={{ marginX: [0, 2], marginY: [0, 2] }}
                />
              ))}
            </ProductListsList>
          )}
          {hasProducts && (
            <ProductsList
              count={products.length}
              sx={{ marginX: [-3, -2], marginY: -2 }}
            >
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  to={link.product(product)}
                  sx={{ marginBottom: [null, null, 3] }}
                />
              ))}
            </ProductsList>
          )}
        </Container>
      ) : null}
    </PreviewLayout>
  );
}

function afterQuery({ event }) {
  const exhibitor = {
    ...event?.exhibitor,
    ...event?.registrations?.nodes?.[0],
  };
  const baseProducts = exhibitor?.products?.nodes || {};
  const products = baseProducts?.map((product) => {
    const registeredProduct = event?.registeredProducts?.nodes?.find(
      (registeredProduct) => registeredProduct?.productId === product?.productId
    );
    return deepMerge(
      {},
      product,
      registeredProduct,
      registeredProduct?.product
    );
  });
  return {
    exhibitor,
    products,
  };
}

const clientQuery = gql`
  query ExhibitorPreviewPage(
    $collectionId: ID!
    $locale: ContentLocale
    $companyId: ID!
  ) {
    event(eventId: $collectionId, locale: $locale) {
      eventId # used for the cache.
      registrations(filter: { companyId: { eq: $companyId } }) {
        nodes {
          ...ExhibitorActions_SingleRegistration
          ...ExhibitorStand_SingleRegistration
        }
      }
      registeredProducts {
        nodes {
          productId
          ...ProductCard_RegisteredProduct
        }
      }
      exhibitor(exhibitorId: $companyId) {
        exhibitorId # used for calculating the cache entry.
        ...BrandBanner
        ...BrandAddressSection
        ...ExhibitorHeaderRow
        ...ExhibitorActions
        ...ExhibitorSections
        ...BrandShopsSection
        products {
          nodes {
            productId # used for the cache.
            id: productId
            slug: productId
            ...useLink_Product
            producer {
              producerId # used for the cache.
            }
            ... on ExtendedProduct {
              exhibitor {
                exhibitorId # needed for the apolloClient cache.
              }
            }
            ...ProductCard
          }
        }
      }
    }
  }
  ${BrandBannerFragment}
  ${BrandAddressSectionFragment}
  ${ExhibitorHeaderRowFragment}
  ${ExhibitorActions.fragment}
  ${ExhibitorSections.fragment}
  ${BrandShopsSectionFragment}
  ${ProductCard.fragment}
  ${useLink.productFragment}
`;
